import type { InvoiceDetailResponse } from '../types/invoice';
import type { NexReconciliationIdentifierMode } from './invoiceNexIdentifierUi';
import { getInvoiceNexIdentifierUi } from './invoiceNexIdentifierUi';

export interface BuildInvoiceReconciliationNextStepArgs {
    invoice: Pick<InvoiceDetailResponse, 'reconciliationIssues' | 'invoiceType' | 'roNumber' | 'nexSubmission'>;
    /** Settings → Invoicing → NEX invoice reference; drives RO vs PO vs identifier copy in guidance. */
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    isReadOnly: boolean;
    /** Live submit gating: line / grand-total arithmetic (non–return-slip invoices). */
    hasArithmeticFlags: boolean;
    /** True when regular or freight rows still lack a required Nexsyis line match for submit. */
    missingRequiredNexMatchesForSubmit: boolean;
    /** Flagged line items after the same ignored-tab filter as the invoice detail page. */
    visibleFlaggedParts: ReadonlyArray<{ issueType: string }>;
    /** When set, user has selected a shop location in Key Identifiers (saved or unsaved). */
    effectiveLocationId?: string;
}

/** Where to scroll the invoice detail right panel when the user clicks the reconciliation banner. */
export type InvoiceReconciliationScrollTarget =
    | 'key-identifiers'
    | 'line-items'
    | 'additional-fees'
    | 'totals'
    | 'reconciliations'
    | 'none';

export interface InvoiceReconciliationNextStep {
    message: string;
    scrollTarget: InvoiceReconciliationScrollTarget;
}

/** Stable DOM ids on `InvoiceRightPanel` (main / reconciliations tab). */
export const INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID: Record<
    Exclude<InvoiceReconciliationScrollTarget, 'none'>,
    string
> = {
    'key-identifiers': 'invoice-recon-section-key-identifiers',
    'line-items': 'invoice-recon-section-line-items',
    'additional-fees': 'invoice-recon-section-additional-fees',
    totals: 'invoice-recon-section-totals',
    reconciliations: 'invoice-recon-section-reconciliations',
};

const NEX_MATCH_ISSUE_TYPES = new Set<string>([
    'missing_in_nex',
    'different_part_names',
    'different_positions',
    'differing_qualifiers',
    'part_number_changed',
]);

/**
 * Single highest-priority next step when the reconciliation banner is shown.
 * Order favors fixing RO / vendor / data before line match, amounts, math, then last submit error.
 */
export function buildInvoiceReconciliationNextStep(
    args: BuildInvoiceReconciliationNextStepArgs
): InvoiceReconciliationNextStep {
    const {
        invoice,
        nexReconciliationIdentifierMode,
        isReadOnly,
        hasArithmeticFlags,
        missingRequiredNexMatchesForSubmit,
        visibleFlaggedParts,
        effectiveLocationId,
    } = args;
    const ri = invoice.reconciliationIssues;
    const idUi = getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode);

    if (isReadOnly) {
        return {
            message:
                'This invoice is read-only. If something still looks wrong, ask an administrator to review or take action in Nexsyis.',
            scrollTarget: 'none',
        };
    }

    const dup406 =
        ri?.nexSubmissionError?.status === 406 ||
        (invoice.nexSubmission?.lastStatus === 'failed' && invoice.nexSubmission?.lastError?.status === 406);

    if (dup406) {
        return {
            message:
                'Nexsyis reported a duplicate or similar rule (HTTP 406). Confirm whether this invoice was already posted; you usually cannot submit the same document twice without fixing the situation in Nexsyis first.',
            scrollTarget: 'key-identifiers',
        };
    }

    if (invoice.invoiceType !== 'return_slip' && ri?.partbayDuplicate) {
        return {
            message:
                'This invoice number and type already exist on a submitted Yaxle invoice for this vendor. If this is a different document, change the invoice number (or vendor), then submit — Submit re-checks the number.',
            scrollTarget: 'key-identifiers',
        };
    }

    const roEmpty = !String(invoice.roNumber ?? '').trim();
    const roNotFound = !!ri?.roNumberNotFound;
    if (ri?.locationNotResolved && !effectiveLocationId) {
        return {
            message:
                'Select the shop location in Key Identifiers. Yaxle could not match this invoice to a location from Nexsyis automatically.',
            scrollTarget: 'key-identifiers',
        };
    }

    if (roEmpty || roNotFound) {
        return {
            message: idUi.reconciliationBannerNotFoundStep,
            scrollTarget: 'key-identifiers',
        };
    }

    if (ri?.uploaderLocationMismatch) {
        return {
            message: ri.uploaderLocationMismatch.message,
            scrollTarget: 'key-identifiers',
        };
    }

    if (ri?.staleNexFolder) {
        return {
            message: ri.staleNexFolder.message,
            scrollTarget: 'key-identifiers',
        };
    }

    if (ri?.vendorNameMismatch) {
        return {
            message:
                'Match or map the supplier to the correct Nexsyis vendor (Settings → Invoicing vendor aliases when invoice wording differs from Nexsyis). A clear vendor match is required before line matching is reliable.',
            scrollTarget: 'key-identifiers',
        };
    }

    if (ri?.noExtractedLineItems) {
        return {
            message:
                'Add or correct line items so the invoice has billable rows—Yaxle could not extract usable lines from the document.',
            scrollTarget: 'line-items',
        };
    }

    if (ri?.specialVendorMatch) {
        const vendorLabel =
            ri.specialVendorMatch.supplierName?.trim() ||
            ri.specialVendorMatch.supplierId;
        return {
            message: `This invoice is from a flagged vendor (${vendorLabel}). Confirm the extracted data, then follow your process to submit when ready.`,
            scrollTarget: 'key-identifiers',
        };
    }

    if (
        ri?.totalFormulaMismatch ||
        ri?.extractedGrandTotalVsAboveLineItemsWithFeesMismatch ||
        ri?.totalMismatch ||
        ri?.subtotalMismatch
    ) {
        return {
            message:
                'Reconcile sums of line totals and other amounts: subtotals, tax, freight, discounts, and fees should roll up to the printed total (adjust lines or totals until the check passes).',
            scrollTarget: 'totals',
        };
    }

    const issueTypes = new Set(visibleFlaggedParts.map((p) => p.issueType));

    if (issueTypes.has('restocking_fee_on_return')) {
        return {
            message:
                'For restocking-on-return flags: update the purchase-order or return line in Nexsyis as required, then refresh Nexsyis data or save and re-check.',
            scrollTarget: 'line-items',
        };
    }

    const needsPoLineMatch =
        missingRequiredNexMatchesForSubmit ||
        Array.from(NEX_MATCH_ISSUE_TYPES).some((t) => issueTypes.has(t));

    if (needsPoLineMatch) {
        if (invoice.invoiceType === 'return_slip') {
            return {
                message:
                    'Match each returned line to the correct Nexsyis return line using the reconciliation control on the row (light bulb), then confirm/save. Every returned item must be matched before Submit.',
                scrollTarget: 'line-items',
            };
        }
        return {
            message:
                'Match each line item and freight row that needs it to the correct Nexsyis PO line (reconciliation / light bulb on the row). Complete every required match before Submit.',
            scrollTarget: 'line-items',
        };
    }

    const priceQtyAmount =
        issueTypes.has('price_mismatch') ||
        issueTypes.has('list_price_mismatch') ||
        issueTypes.has('list_price_equals_buy_price') ||
        issueTypes.has('discount_below_expected') ||
        issueTypes.has('quantity_mismatch') ||
        issueTypes.has('amount_mismatch') ||
        issueTypes.has('amount_mismatch_multiple_nex_lines') ||
        issueTypes.has('amount_mismatch_single_nex_line') ||
        issueTypes.has('amount_mismatch_nex_structure');

    if (priceQtyAmount) {
        return {
            message:
                issueTypes.has('discount_below_expected')
                    ? 'Review lines flagged for discount below expected (list vs buy vs Discount Flag settings), align quantities and prices with Nexsyis or confirm intentional differences, then submit.'
                    : 'Align quantities, list prices, buy prices, and extensions with Nexsyis (or document intentional differences per your policy), then submit.',
            scrollTarget: 'line-items',
        };
    }

    const needsMathStep =
        (invoice.invoiceType !== 'return_slip' && hasArithmeticFlags) ||
        (invoice.invoiceType === 'return_slip' && issueTypes.has('calculation_mismatch'));

    if (needsMathStep) {
        if (invoice.invoiceType === 'return_slip') {
            return {
                message:
                    'Fix row-level math where flagged: quantity × unit price should equal the line total on each credited line.',
                scrollTarget: 'line-items',
            };
        }
        return {
            message:
                'Fix arithmetic across Main, Freight, Fuel, fees, and discounts: each line should satisfy quantity × unit price = line total, and the grid should roll up to the invoice total so Submit can unlock.',
            scrollTarget: 'line-items',
        };
    }

    if (ri?.nexSubmissionError && typeof ri.nexSubmissionError.status === 'number' && ri.nexSubmissionError.status !== 406) {
        return {
            message:
                'Review the Nexsyis error message on this invoice, correct RO, vendor, lines, or amounts as indicated, save, then try Submit again.',
            scrollTarget: 'key-identifiers',
        };
    }

    return {
        message:
            'Review Key Identifiers and each highlighted line in Line Items (and the Key Reconciliations tab if needed), save your fixes, then try Submit when flags clear.',
        scrollTarget: 'reconciliations',
    };
}
